/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/settings.scss';

import React, {useContext, useEffect, useMemo, useState} from 'react';
import {AccessKey, SettingsTab} from '../../util/interfaces';
import classNames from 'classnames';
import {sendGaEvent} from "../../util/analytics";
import {showDialog} from '@dvrd/dvr-controls';
import {Location, Outlet, useLocation, useNavigate} from 'react-router';
import {AppContext} from "../misc/appContext";
import {userHasRight} from "../../util/accessUtil";
import {requestPageChangePermission} from "../../util/pageChanger";

type Tab = { label: string; tab: SettingsTab; requireRight: 'allow' | AccessKey; path: string; userBetaKey?: string; };
const TABS: Array<Tab> = [
    {label: 'Algemeen', tab: SettingsTab.COMMON, requireRight: 'allow', path: 'algemeen'},
    {
        label: 'Gebruikers',
        tab: SettingsTab.ACCOUNT_USERS,
        requireRight: AccessKey.PAGE_ACCOUNT_USERS,
        path: 'gebruikers',
    },
    {label: 'Beveiliging', tab: SettingsTab.PASSWORD, requireRight: 'allow', path: 'beveiliging'},
    {
        label: 'Koppeling Bol',
        tab: SettingsTab.CONNECTION,
        requireRight: AccessKey.PAGE_SETTINGS_API,
        path: 'koppeling'
    },
    {label: 'Producten & BTW', tab: SettingsTab.VAT, requireRight: AccessKey.PAGE_SETTINGS_PRODUCTS, path: 'producten'},
    {
        label: 'Factuurinstellingen',
        tab: SettingsTab.INVOICES,
        requireRight: AccessKey.PAGE_SETTINGS_INVOICE,
        path: 'factuur-instellingen'
    },
    {label: 'E-mails', tab: SettingsTab.EMAIL, requireRight: AccessKey.PAGE_SETTINGS_EMAILS, path: 'emails'},
    {label: 'Exporteren', tab: SettingsTab.EXPORT, requireRight: AccessKey.PAGE_SETTINGS_EXPORT, path: 'exporteren'},
    {
        label: 'Abonnement',
        tab: SettingsTab.SUBSCRIPTION,
        requireRight: AccessKey.PAGE_SETTINGS_SUBSCRIPTION,
        path: 'abonnement'
    },
];

function getTabFromLocation(location: Location) {
    const path = location.pathname;
    if (path.endsWith('algemeen')) return SettingsTab.COMMON;
    else if (path.endsWith('gebruikers')) return SettingsTab.ACCOUNT_USERS;
    else if (path.endsWith('beveiliging')) return SettingsTab.PASSWORD;
    else if (path.endsWith('koppeling')) return SettingsTab.CONNECTION;
    else if (path.endsWith('producten')) return SettingsTab.VAT;
    else if (path.endsWith('factuur-instellingen')) return SettingsTab.INVOICES;
    else if (path.endsWith('emails')) return SettingsTab.EMAIL;
    else if (path.endsWith('exporteren')) return SettingsTab.EXPORT;
    else if (path.endsWith('abonnement')) return SettingsTab.SUBSCRIPTION;
    return null;
}

export default function Settings() {
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const location = useLocation();
    const [tab, setTab] = useState(getTabFromLocation(location) ?? SettingsTab.COMMON);
    const tabs = useMemo(() => {
        return TABS.filter(
            (tab: Tab) => {
                if (tab.userBetaKey && !user?.isSuperAdmin() && user?.getFlex(tab.userBetaKey) !== true) return false;
                return tab.requireRight === 'allow' || userHasRight(context.accountContext.account, tab.requireRight);
            });
    }, [context.accountContext.account, context.userContext.user]);
    const navigate = useNavigate();

    function onChangeTab(tab: SettingsTab) {
        return function () {
            requestPageChangePermission(() => {
                sendGaEvent('Click', 'Instellingen', `Tab ${getTabLabel(tab)}`);
                changeTab(tab);
            });
        }
    }

    function changeTab(tab: SettingsTab) {
        if (tab === SettingsTab.SUBSCRIPTION && context.userContext.user?.isDemoAccount === true) {
            showDialog('Abonnement instellingen zijn niet beschikbaar in het demo account.', 'Demo account');
            return;
        }
        const path = TABS.find((_tab) => _tab.tab === tab)?.path;
        if (!path) return;
        setTab(tab);
        navigate(`/instellingen/${path}`);
    }

    function getTabLabel(tab: SettingsTab) {
        for (const availableTab of tabs)
            if (availableTab.tab === tab) return availableTab.label;
        return null;
    }

    function renderTab(tabData: { label: string, tab: SettingsTab }, index: number) {
        const active = tab === tabData.tab;
        return (
            <div key={index} className={classNames('tab', active && 'active')} onClick={onChangeTab(tabData.tab)}>
                <label className='tab-label'>{tabData.label}</label>
            </div>
        )
    }

    useEffect(() => {
        setTab(getTabFromLocation(location) ?? tab);
    }, [location.pathname]);

    return (
        <div className='settings'>
            <div className='setting-tabs-container'>
                <div className='tabs'>
                    {tabs.map(renderTab)}
                </div>
                <div className='settings-tab-content'>
                    <Outlet/>
                </div>
            </div>
        </div>
    )
}
